import React from "react";
import { Mail, MapPin, Instagram, Check } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Grain from "../components/Grain";

export default function Contact() {
  const [form, setForm] = React.useState({ nom: "", email: "", telephone: "", sujet: "Séance photo", message: "" });
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (e) {
      setErr("Une erreur est survenue. Réessaie ou écris-moi directement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 section-dark">
      <section className="px-6 md:px-12 py-20 relative overflow-hidden" data-testid="contact-hero">
        <Grain />
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] glow-amber-soft pointer-events-none" />
        <div className="max-w-[1280px] mx-auto relative z-10">
          <div className="eyebrow mb-4">Contact</div>
          <h1 className="h-display text-6xl md:text-8xl">Parlons<br /><span className="italic-accent">de toi.</span></h1>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-32" data-testid="contact-grid">
        <div className="max-w-[1280px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 space-y-8">
            <p className="text-text-cream/70 font-light text-lg leading-relaxed">
              Une séance, un événement, une idée folle ? Écris-moi. Je réponds en moins de 24h.
            </p>
            <div className="space-y-5">
              <div className="flex items-start gap-3" data-testid="contact-address">
                <MapPin size={18} className="text-accent-deep mt-1" strokeWidth={1.5}/>
                <div className="font-light">Route de Crochy 20<br />1024 Écublens — Suisse</div>
              </div>
              <a href="mailto:studioeuclides25@gmail.com" className="flex items-center gap-3 hover:text-accent-light break-all" data-testid="contact-email">
                <Mail size={18} className="text-accent-deep" strokeWidth={1.5}/>
                <span className="font-light">studioeuclides25@gmail.com</span>
              </a>
              <a href="https://www.instagram.com/studio_euclides" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-accent-light" data-testid="contact-instagram">
                <Instagram size={18} className="text-accent-deep" strokeWidth={1.5}/>
                <span className="font-light">@studio_euclides</span>
              </a>
              <Link to="/reserver-seance" className="btn-primary" data-testid="contact-book-button">Réserver</Link>
            </div>
            <div className="pt-6 border-t border-white/10">
              <div className="eyebrow mb-2">Horaires studio</div>
              <div className="text-text-cream/60 text-sm font-light leading-relaxed" data-testid="contact-hours">
                Lun–Ven · sur rendez-vous<br/>
                <span data-testid="contact-hours-saturday">Samedi · 10h–20h</span><br/>
                <span data-testid="contact-hours-sunday">Dimanche · fermé</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <div className="border border-accent-deep/40 p-10 text-center" data-testid="contact-success">
                <Check size={32} className="text-accent-deep mx-auto mb-4" strokeWidth={1.5}/>
                <h3 className="font-serif text-3xl italic">Message envoyé.</h3>
                <p className="mt-4 text-text-cream/70 font-light">Je te réponds dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow mb-2 block">Nom</label>
                    <input data-testid="input-nom" required className="input-editorial" value={form.nom} onChange={(e)=>setForm({...form, nom:e.target.value})} />
                  </div>
                  <div>
                    <label className="eyebrow mb-2 block">Email</label>
                    <input data-testid="input-email" type="email" required className="input-editorial" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow mb-2 block">Téléphone</label>
                    <input data-testid="input-telephone" className="input-editorial" value={form.telephone} onChange={(e)=>setForm({...form, telephone:e.target.value})} />
                  </div>
                  <div>
                    <label className="eyebrow mb-2 block">Sujet</label>
                    <select data-testid="input-sujet" className="input-editorial bg-dark" value={form.sujet} onChange={(e)=>setForm({...form, sujet:e.target.value})}>
                      <option>Séance photo</option>
                      <option>Mariage / événement</option>
                      <option>Maternité / Nouveau-né</option>
                      <option>Personal Branding</option>
                      <option>L'Atelier Lumière</option>
                      <option>Autre</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="eyebrow mb-2 block">Message</label>
                  <textarea data-testid="input-message" required rows={6} className="input-editorial resize-none" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} />
                </div>
                {err && <div className="text-red-400 text-sm">{err}</div>}
                <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
                  {loading ? "Envoi…" : "Envoyer le message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
